import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Trash2, RefreshCw, Plus, Pencil, X, CheckCircle2,
  Star, StarOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuthStore, useLanguageStore, getLocalized } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import LocalizedField from '../components/admin/LocalizedField';
import TranslateAllButton from '../components/admin/TranslateAllButton';
import AdminHeader from '../components/admin/AdminHeader';
import { useTranslation } from '../i18n/translations';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = [
  { id: '', label: '— None —' },
  { id: 'kitchens', label: 'Kitchens' },
  { id: 'wardrobes', label: 'Wardrobes' },
  { id: 'doors', label: 'Doors' },
  { id: 'partitions', label: 'Partitions' },
  { id: 'day_systems', label: 'Day Systems' },
  { id: 'wall_panels', label: 'Wall Panels' },
  { id: 'soft_furniture', label: 'Soft Furniture' },
  { id: 'ready_solutions', label: 'Ready Solutions' },
  { id: 'custom_furniture', label: 'Custom' },
];

const getEmptyCollection = () => ({
  slug: '',
  name: { en: '', nl: '', ro: '', ru: '' },
  subtitle: { en: '', nl: '', ro: '', ru: '' },
  description: { en: '', nl: '', ro: '', ru: '' },
  category: '',
  is_hero: true,
  hero_order: 0,
  is_active: true,
});

const coerceLocalized = (v) => {
  if (!v) return { en: '', nl: '', ro: '', ru: '' };
  if (typeof v === 'string') return { en: v, nl: '', ro: '', ru: '' };
  return { en: '', nl: '', ro: '', ru: '', ...v };
};

const slugify = (s) => (s || '').toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

async function uploadToCloudinary(file, token, folder = 'collections') {
  const sigRes = await fetch(`${API}/api/cloudinary/signature?folder=${folder}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!sigRes.ok) throw new Error('Signature failed');
  const sig = await sigRes.json();
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  
  const upRes = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
    method: 'POST', body: form,
  });
  if (!upRes.ok) throw new Error('Upload failed');
  return await upRes.json();
}

const CollectionFormModal = ({ open, onClose, onSaved, collection, token }) => {
  const isEdit = Boolean(collection?.id);
  const [form, setForm] = useState(getEmptyCollection());
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          slug: collection.slug || '',
          name: coerceLocalized(collection.name),
          subtitle: coerceLocalized(collection.subtitle),
          description: coerceLocalized(collection.description),
          category: collection.category || '',
          is_hero: !!collection.is_hero,
          hero_order: collection.hero_order || 0,
          is_active: collection.is_active !== false,
        });
        setSlugTouched(true);
      } else {
        setForm(getEmptyCollection());
        setSlugTouched(false);
      }
    }
  }, [open, isEdit, collection]);

  const handleNameChange = (nextName) => {
    setForm((prev) => {
      const next = { ...prev, name: nextName };
      if (!isEdit && !slugTouched && nextName.en) {
        next.slug = slugify(nextName.en);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.slug.trim()) { toast.error('Slug is required'); return; }
    if (!form.name.en.trim()) { toast.error('Name (EN) is required'); return; }

    setSaving(true);
    try {
      const body = { ...form, category: form.category || null, hero_order: Number(form.hero_order) || 0 };
      const url = isEdit
        ? `${API}/api/admin/collections/${collection.id}`
        : `${API}/api/admin/collections`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Save failed');
      }
      toast.success(isEdit ? 'Collection updated' : 'Collection created');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-neutral-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="collection-form-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl border border-neutral-200 shadow-2xl rounded-none"
        style={{ backgroundColor: '#ffffff' }}
      >
        {/* ЭЛИТНЫЙ ФИКС: Сверхспецифичные CSS-инъекции для идеальной полировки кнопок и устранения дефектов */}
        <style>{`
          .fedyk-modal-header h2 {
            color: #000000 !important;
          }
          .fedyk-modal-header p {
            color: #737373 !important;
          }
          
          /* Бронебойный оверрайд кнопки Cancel (без наведения и с наведением) */
          body div[data-testid="collection-form-modal"] button.fedyk-cancel-btn,
          body .fedyk-cancel-btn {
            background-color: #ffffff !important;
            color: #000000 !important;
            border: 1px solid #d4d4d4 !important;
            transition: all 0.15s ease-in-out !important;
            box-shadow: none !important;
            cursor: pointer !important;
          }
          body div[data-testid="collection-form-modal"] button.fedyk-cancel-btn:hover,
          body .fedyk-cancel-btn:hover {
            background-color: #f5f5f5 !important;
            color: #000000 !important;
            border-color: #a3a3a3 !important;
          }
          
          /* Бронебойный оверрайд кнопки Save / Initialize */
          body div[data-testid="collection-form-modal"] button.fedyk-primary-btn,
          body .fedyk-primary-btn {
            background-color: #000000 !important;
            color: #ffffff !important;
            border: 1px solid #000000 !important;
            transition: all 0.15s ease-in-out !important;
            cursor: pointer !important;
          }
          body div[data-testid="collection-form-modal"] button.fedyk-primary-btn:hover,
          body .fedyk-primary-btn:hover {
            background-color: #262626 !important;
            border-color: #262626 !important;
            color: #ffffff !important;
          }

          /* ЗАЩИТНЫЙ РЕСЕТ: возвращает кнопкам выбора языков (встроенным в шапки полей) их чистый бесструктурный вид без рамок и лишнего margin */
          div[data-testid="collection-form-modal"] .flex.items-center.justify-between button,
          div[data-testid="collection-form-modal"] .flex.justify-between.items-center button {
            border: none !important;
            background-color: transparent !important;
            background: none !important;
            color: #737373 !important;
            font-size: 11px !important;
            font-weight: 500 !important;
            padding: 2px 6px !important;
            margin-top: 0px !important;
            height: auto !important;
            display: inline-flex !important;
            align-items: center !important;
            gap: 4px !important;
            box-shadow: none !important;
            cursor: pointer !important;
          }
          div[data-testid="collection-form-modal"] .flex.items-center.justify-between button:hover,
          div[data-testid="collection-form-modal"] .flex.justify-between.items-center button:hover {
            color: #141414 !important;
            background-color: transparent !important;
          }

          /* Фикс индивидуальной кнопки "AUTO TRANSLATE" под полями LocalizedField (исключаем кнопки выбора языков в шапках полей!) */
          div[data-testid="collection-form-modal"] .p-6 > div > button.text-xs.text-neutral-400,
          div[data-testid="collection-form-modal"] .space-y-6 > div > button.text-xs:not(.flex),
          div[data-testid="collection-form-modal"] button[class*="translate-field"] {
            border-radius: 0px !important;
            font-size: 9px !important;
            font-weight: 600 !important;
            letter-spacing: 0.12em !important;
            text-transform: uppercase !important;
            color: #000000 !important; /* Черный текст по умолчанию */
            border: 1px solid #d4d4d4 !important;
            background-color: #ffffff !important; /* Белый фон по умолчанию */
            padding: 6px 12px !important;
            margin-top: 8px !important;
            margin-bottom: 4px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            height: 30px !important;
            transition: all 0.15s ease-in-out !important;
            cursor: pointer !important;
            box-shadow: none !important;
          }
          div[data-testid="collection-form-modal"] .p-6 > div > button.text-xs.text-neutral-400:hover,
          div[data-testid="collection-form-modal"] .space-y-6 > div > button.text-xs:not(.flex):hover,
          div[data-testid="collection-form-modal"] button[class*="translate-field"]:hover {
            color: #000000 !important;
            border-color: #141414 !important;
            background-color: #f5f5f5 !important; /* Светло-серый фон при наведении */
          }

          /* Фикс вкладок выбора языков (EN, NL, RO, RU) в LocalizedField */
          div[data-testid="collection-form-modal"] .flex.items-center.gap-1 button,
          div[data-testid="collection-form-modal"] button[data-testid^="lang-"] {
            border-radius: 0px !important;
            border: 1px solid #d4d4d4 !important;
            background-color: #ffffff !important;
            color: #000000 !important; /* Белый фон, черный текст по умолчанию */
            font-size: 10px !important;
            font-weight: 500 !important;
            letter-spacing: 0.1em !important;
            padding: 4px 8px !important;
            text-transform: uppercase !important;
            height: auto !important;
            transition: all 0.15s ease-in-out !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          div[data-testid="collection-form-modal"] .flex.items-center.gap-1 button:hover,
          div[data-testid="collection-form-modal"] button[data-testid^="lang-"]:hover {
            color: #000000 !important;
            border-color: #a3a3a3 !important;
            background-color: #f5f5f5 !important; /* Светло-серый фон при наведении */
          }
          div[data-testid="collection-form-modal"] .flex.items-center.gap-1 button.bg-black,
          div[data-testid="collection-form-modal"] .flex.items-center.gap-1 button[class*="bg-black"],
          div[data-testid="collection-form-modal"] .flex.items-center.gap-1 button[class*="active"],
          div[data-testid="collection-form-modal"] button[data-testid^="lang-"][class*="bg-black"] {
            background-color: #141414 !important;
            color: #ffffff !important;
            border-color: #141414 !important;
          }

          /* Стилизация выпадающего списка выбора языков */
          div[data-testid="collection-form-modal"] [role="menu"],
          div[data-testid="collection-form-modal"] [class*="dropdown-menu"] {
            background-color: #ffffff !important;
            border: 1px solid #e5e5e5 !important;
            border-radius: 0px !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
            padding: 4px 0 !important;
          }
          div[data-testid="collection-form-modal"] [role="menuitem"],
          div[data-testid="collection-form-modal"] [class*="menuitem"],
          div[data-testid="collection-form-modal"] [class*="dropdown-item"] {
            color: #141414 !important;
            background-color: #ffffff !important;
            font-size: 11px !important;
            padding: 6px 12px !important;
            transition: all 0.15s ease-in-out !important;
            cursor: pointer !important;
          }
          div[data-testid="collection-form-modal"] [role="menuitem"]:hover,
          div[data-testid="collection-form-modal"] [class*="menuitem"]:hover,
          div[data-testid="collection-form-modal"] [class*="dropdown-item"]:hover {
            background-color: #f5f5f5 !important; /* Светло-серый фон при наведении */
            color: #000000 !important;
          }

          /* Фикс кнопки глобального автоперевода "Translate All" */
          div[data-testid="collection-form-modal"] button[data-testid="coll-translate-all"],
          div[data-testid="collection-form-modal"] button.fedyk-translate-all-btn {
            border-radius: 0px !important;
            background-color: #ffffff !important; /* Белый фон по умолчанию */
            color: #000000 !important; /* Черный текст по умолчанию */
            border: 1px solid #141414 !important;
            font-size: 10px !important;
            font-weight: 600 !important;
            letter-spacing: 0.15em !important;
            text-transform: uppercase !important;
            padding: 8px 16px !important;
            height: 36px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 6px !important;
            transition: all 0.15s ease-in-out !important;
            box-shadow: none !important;
            cursor: pointer !important;
          }
          div[data-testid="collection-form-modal"] button[data-testid="coll-translate-all"]:hover,
          div[data-testid="collection-form-modal"] button.fedyk-translate-all-btn:hover {
            background-color: #f5f5f5 !important; /* Светло-серый фон при наведении */
            color: #000000 !important;
            border-color: #141414 !important;
          }

          /* Настройка кнопки закрытия Х в шапке модального окна */
          div[data-testid="collection-form-modal"] button[data-testid="collection-form-close"] {
            border-radius: 0px !important;
            border: 1px solid transparent !important;
            color: #a3a3a3 !important;
            padding: 6px !important;
            transition: all 0.15s ease-in-out !important;
            background-color: transparent !important;
            cursor: pointer !important;
          }
          div[data-testid="collection-form-modal"] button[data-testid="collection-form-close"]:hover {
            color: #141414 !important;
            border-color: #e5e5e5 !important;
            background-color: #fafafa !important;
          }
        `}</style>

        <div className="flex items-center justify-between border-b border-neutral-200 p-6 fedyk-modal-header" style={{ backgroundColor: '#ffffff', borderColor: '#e5e5e5' }}>
          <div>
            <p className="text-xs font-mono uppercase tracking-widest mb-1">
              {isEdit ? 'Update Hub' : 'Initialization'}
            </p>
            <h2 className="text-2xl font-extralight">
              {isEdit ? getLocalized(form.name, language) || form.slug : 'Create Architecture Collection'}
            </h2>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 transition-colors cursor-pointer" 
            style={{ backgroundColor: 'transparent' }}
            data-testid="collection-form-close" 
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        
        <div className="px-6 pt-4 -mb-2 flex justify-end bg-white">
          <TranslateAllButton
            testid="coll-translate-all"
            token={token}
            fields={[
              { value: form.name, onChange: (v) => setForm((p) => ({ ...p, name: v })) },
              { value: form.subtitle, onChange: (v) => setForm((p) => ({ ...p, subtitle: v })) },
              { value: form.description, onChange: (v) => setForm((p) => ({ ...p, description: v })) },
            ]}
          />
        </div>

        <div className="p-6 space-y-6 bg-white text-left">
          <LocalizedField
            label="Collection System Name *"
            required
            value={form.name}
            onChange={handleNameChange}
            testid="coll-form-name"
            placeholder="e.g. INTEGRA System / RADIAL Series"
            token={token}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase font-mono mb-2" style={{ color: '#737373' }}>Slug Routing URL *</label>
              <Input
                value={form.slug}
                onChange={(e) => { setForm({ ...form, slug: slugify(e.target.value) }); setSlugTouched(true); }}
                placeholder="e.g. air-line"
                className="rounded-none h-11 font-mono text-sm border-neutral-200 focus-visible:border-black font-light text-neutral-900"
                style={{ backgroundColor: '#ffffff', color: '#000000' }}
                data-testid="coll-form-slug"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase font-mono mb-2" style={{ color: '#737373' }}>Default Global Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-11 px-3 border border-neutral-200 text-sm w-full cursor-pointer font-light bg-white text-neutral-900 focus:outline-none focus:border-black rounded-none"
                style={{ color: '#000000', backgroundColor: '#ffffff' }}
                data-testid="coll-form-category"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id} style={{ color: '#000000', backgroundColor: '#ffffff' }}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <LocalizedField
            label='Subtitle Framework (e.g. "Bespoke sliding partitions")'
            value={form.subtitle}
            onChange={(next) => setForm({ ...form, subtitle: next })}
            testid="coll-form-subtitle"
            placeholder="System architectural framework"
            token={token}
          />

          <LocalizedField
            label="Curated Overview Description"
            multiline rows={4}
            value={form.description}
            onChange={(next) => setForm({ ...form, description: next })}
            testid="coll-form-description"
            placeholder="Bespoke engineering layout and architectural specifications of the portfolio..."
            token={token}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center pt-2 border-t border-neutral-100">
            <div>
              <label className="block text-[10px] tracking-[0.25em] uppercase font-mono mb-2" style={{ color: '#737373' }}>Spotlight Banner Sort Order</label>
              <Input
                type="number"
                value={form.hero_order}
                onChange={(e) => setForm({ ...form, hero_order: e.target.value })}
                className="rounded-none h-11 border-neutral-200 focus-visible:border-black text-neutral-900 font-light"
                style={{ backgroundColor: '#ffffff', color: '#000000' }}
                data-testid="coll-form-hero-order"
              />
            </div>
            <div className="flex flex-col gap-3 md:pt-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer font-normal select-none" style={{ color: '#262626' }}>
                <input
                  type="checkbox"
                  checked={form.is_hero}
                  onChange={(e) => setForm({ ...form, is_hero: e.target.checked })}
                  className="accent-black w-4 h-4 cursor-pointer"
                  data-testid="coll-form-is-hero"
                />
                Pin on Homepage Hero Slider
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer font-normal select-none" style={{ color: '#262626' }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="accent-black w-4 h-4 cursor-pointer"
                  data-testid="coll-form-is-active"
                />
                Published & Active
              </label>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-neutral-200 p-6 bg-white">
          <button 
            type="button"
            className="rounded-none text-xs h-10 px-6 font-medium uppercase tracking-wider flex items-center justify-center cursor-pointer select-none fedyk-cancel-btn" 
            onClick={onClose} 
            disabled={saving} 
            data-testid="collection-form-cancel"
            style={{ height: '40px' }}
          >
            {t?.common?.cancel || 'Cancel'}
          </button>
          
          <button 
            type="button"
            onClick={handleSave} 
            disabled={saving} 
            className="px-6 text-xs rounded-none uppercase tracking-widest flex items-center justify-center font-medium fedyk-primary-btn" 
            style={{ height: '40px' }}
            data-testid="collection-form-save"
          >
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Initialize')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const AdminCollectionsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const [hydrated, setHydrated] = useState(false);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileInputsRef = useRef({});

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/collections' } });
    }
  }, [hydrated, navigate]);

  const fetchCollections = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) { logout(); navigate('/admin'); return; }
      const data = await res.json();
      setCollections(data.collections || []);
    } catch {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => { if (hydrated) fetchCollections(); }, [hydrated, fetchCollections]);

  const handleImageUpload = async (coll, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error('Max file size is 15 MB'); return; }

    setUploadingId(coll.id);
    try {
      const up = await uploadToCloudinary(file, token);
      const res = await fetch(`${API}/api/admin/collections/${coll.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ image_url: up.secure_url, image_public_id: up.public_id }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Image updated');
      fetchCollections();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingId(null);
      if (fileInputsRef.current[coll.id]) fileInputsRef.current[coll.id].value = '';
    }
  };

  const handleDelete = async (coll) => {
    if (!window.confirm(`Delete collection "${getLocalized(coll.name, language) || coll.slug}"? Products linked to it will be detached safely.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/collections/${coll.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Collection deleted');
      fetchCollections();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleHero = async (coll) => {
    try {
      const res = await fetch(`${API}/api/admin/collections/${coll.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_hero: !coll.is_hero }),
      });
      if (!res.ok) throw new Error();
      fetchCollections();
    } catch {
      toast.error('Failed to update');
    }
  };

  if (!hydrated) return <div className="min-h-screen flex items-center justify-center font-mono text-xs text-neutral-400 bg-white">Loading structural nodes…</div>;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-left font-light">
      <SEO title="Collections · Admin" description="Manage collections and homepage hero cards" path="admin/collections" />
      <AdminHeader active="collections" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-neutral-200 pb-6">
          <div>
            <p className="text-[10px] font-medium tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">CMS · Architecture</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">System Collections</h1>
            <p className="text-neutral-500 mt-4 max-w-xl text-sm leading-relaxed font-light">
              Manage core structural lines (AIR LINE, INTEGRA, RADIAL) and compile primary showcases onto the digital environment framework.
            </p>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchCollections} 
              disabled={loading} 
              className="bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium uppercase tracking-wider" 
              style={{ height: '40px' }}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />Refresh Hub
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setEditing(null); setFormOpen(true); }} 
              className="bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium uppercase tracking-wider" 
              style={{ height: '40px' }}
              data-testid="admin-collections-new"
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />New Collection
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-neutral-400 text-xs font-mono">Loading dynamic clusters…</div>
        ) : collections.length === 0 ? (
          <div className="py-24 text-center text-neutral-500 border border-neutral-200 bg-white shadow-sm rounded-none text-sm font-light">
            No system collections generated yet.
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setEditing(null); setFormOpen(true); }} 
                className="bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium mx-auto uppercase tracking-wider" 
                style={{ height: '40px' }}
              >
                <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Initialize First Portfolio Node
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {collections.map((c) => {
              const isUploading = uploadingId === c.id;
              return (
                <div
                  key={c.id}
                  data-testid={`collection-card-${c.id}`}
                  className="flex flex-col bg-white border border-neutral-200 p-5 shadow-sm rounded-none text-left justify-between h-auto min-h-[460px]"
                >
                  <div>
                    <div className="h-[240px] w-full bg-neutral-50 relative overflow-hidden mb-5 border border-neutral-100">
                      {c.image_url ? (
                        <img src={c.image_url} alt={getLocalized(c.name, language)} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-neutral-400">
                          <Upload className="w-6 h-6 mb-3" strokeWidth={1} />
                          <span className="text-[10px] uppercase tracking-wider font-mono">No imagery pack</span>
                        </div>
                      )}
                      <div className="absolute top-3 left-3 flex flex-col gap-1">
                        {c.is_hero && (
                          <span className="bg-white/95 backdrop-blur-sm border border-neutral-200 px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase flex items-center gap-1.5 font-bold text-gray-800">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" strokeWidth={2.5} />Hero Pinned · Order: {c.hero_order}
                          </span>
                        )}
                        {!c.is_active && (
                          <span className="bg-black text-white px-2.5 py-1 text-[9px] tracking-[0.2em] uppercase font-bold">Draft Context</span>
                        )}
                      </div>
                    </div>

                    <p className="text-[11px] font-mono uppercase tracking-wider text-neutral-400 mb-0.5">{getLocalized(c.subtitle, language) || 'Architecture System'}</p>
                    <h3 className="text-xl font-light text-neutral-900 tracking-tight leading-tight">{getLocalized(c.name, language)}</h3>
                    <p className="text-[10px] text-neutral-400 tracking-wide font-mono mt-1">Routing node: /{c.slug}</p>
                  </div>

                  <input
                    key={c.id}
                    ref={(el) => { fileInputsRef.current[c.id] = el; }}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(c, e.target.files?.[0])}
                    data-testid={`coll-file-input-${c.id}`}
                  />
                  <div className="flex items-center gap-2 mt-6 border-t border-neutral-100 pt-4">
                    <Button
                      onClick={() => fileInputsRef.current[c.id]?.click()}
                      disabled={isUploading}
                      variant="outline"
                      className="flex-1 rounded-none text-xs h-9 bg-white text-neutral-900 border-neutral-200 transition-colors uppercase tracking-wider"
                      data-testid={`coll-upload-btn-${c.id}`}
                    >
                      <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />
                      {isUploading ? '...' : (c.image_url ? 'Replace' : 'Upload Cover')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => toggleHero(c)}
                      className="w-11 rounded-none text-xs h-9 bg-white text-neutral-900 border-neutral-200 p-0 transition-colors"
                      title={c.is_hero ? 'Remove from hero' : 'Add to hero'}
                      data-testid={`coll-hero-toggle-${c.id}`}
                    >
                      {c.is_hero ? <StarOff className="w-3.5 h-3.5 text-amber-500" strokeWidth={1.5} /> : <Star className="w-3.5 h-3.5 text-neutral-400" strokeWidth={1.5} />}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setEditing(c); setFormOpen(true); }}
                      className="w-11 rounded-none text-xs h-9 bg-white text-neutral-900 border-neutral-200 p-0 transition-colors"
                      data-testid={`coll-edit-${c.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(c)}
                      className="w-11 rounded-none text-xs h-9 bg-white text-red-600 border-neutral-200 p-0 hover:bg-rose-50 transition-colors"
                      data-testid={`coll-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {formOpen && (
          <CollectionFormModal
            open={formOpen}
            onClose={() => setFormOpen(false)}
            onSaved={fetchCollections}
            collection={editing}
            token={token}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCollectionsPage;
